import {
  Box,
  type ColumnsType,
  CountAmountCell,
  Divider,
  Flex,
  Table,
  Text,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { SubProjectForKSModel } from '@/features/project/projects/_models/sub-project';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type SubProjectForKs = z.infer<typeof SubProjectForKSModel>;
type SubProjectForKsColumns = ColumnsType<SubProjectForKs>;

type Props = {
  fundBusinessForLocalGovernmentSummaries: SubProjectForKs[] | undefined;
  previousFiscalYear: number;
};

const PINPOINT_SHEET_TYPE = 'KS_LOCAL_GOVERNMENT';

export const FundBusinessTable: FC<Props> = ({
  fundBusinessForLocalGovernmentSummaries,
  previousFiscalYear,
}) => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const fundBusinessColumns = useMemo<SubProjectForKsColumns>(
    () => [
      {
        key: 'decidedFundBusinessForLocalGovernment',
        title: t('decision-on-project-implementation-with-year', {
          year: previousFiscalYear,
        }),
        children: [
          {
            key: 'decidedNumberOfSubsidy',
            title: t('fund-business-subsidy'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.decidedNumberOfSubsidy}
                  amount={fundBusinessForLocalGovernment.decidedAmountOfSubsidy}
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeDecidedAmountOfSubsidyCount
                  }
                />
              ),
          },
          {
            key: 'decidedNumberOfLoan',
            title: t('investment'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.decidedNumberOfInvestment
                  }
                  amount={
                    fundBusinessForLocalGovernment.decidedAmountOfInvestment
                  }
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeDecidedAmountOfInvestmentCount
                  }
                />
              ),
          },
          {
            key: 'decidedNumberOfLoan',
            title: t('loan'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.decidedNumberOfLoan}
                  amount={fundBusinessForLocalGovernment.decidedAmountOfLoan}
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeDecidedAmountOfLoanCount
                  }
                />
              ),
          },
          {
            key: 'decidedNumberOfDebtGuarantee',
            title: t('debt-guarantee'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.decidedNumberOfDebtGuarantee
                  }
                  amount={
                    fundBusinessForLocalGovernment.decidedAmountOfDebtGuarantee
                  }
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeDecidedAmountOfDebtGuaranteeCount
                  }
                />
              ),
          },
          {
            key: 'decidedNumberOfOthers',
            title: t('fund-business-for-local-government-others'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.decidedNumberOfOthers}
                  amount={fundBusinessForLocalGovernment.decidedAmountOfOthers}
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeDecidedAmountOfOthersCount
                  }
                />
              ),
          },
        ],
      },
      {
        key: 'balancedFundBusinessForLocalGovernment',
        title: t('fund-business-loan-balance-title-with-year', {
          year: previousFiscalYear,
        }),
        children: [
          {
            key: 'balanceNumberOfSubsidy',
            title: t('investment'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.balanceNumberOfInvestment
                  }
                  amount={
                    fundBusinessForLocalGovernment.balanceAmountOfInvestment
                  }
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeBalanceAmountOfInvestmentCount
                  }
                />
              ),
          },
          {
            key: 'balanceNumberOfLoan',
            title: t('loan'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.balanceNumberOfLoan}
                  amount={fundBusinessForLocalGovernment.balanceAmountOfLoan}
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeBalanceAmountOfLoanCount
                  }
                />
              ),
          },
          {
            key: 'balanceNumberOfDebtGuarantee',
            title: t('debt-guarantee'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.balanceNumberOfDebtGuarantee
                  }
                  amount={
                    fundBusinessForLocalGovernment.balanceAmountOfDebtGuarantee
                  }
                  unit={unit}
                  isDebugMode={isDebugMode}
                  negativeCount={
                    fundBusinessForLocalGovernment.negativeBalanceAmountOfDebtGuaranteeCount
                  }
                />
              ),
          },
        ],
      },
    ],
    [t, previousFiscalYear, unit, isDebugMode],
  );

  return (
    <Flex vertical gap={8}>
      <Flex align="center">
        <Text text={t('fund-business')} />
        <Divider type="vertical" />
        <Text color="secondary" text={t('count-and-amount')} />
      </Flex>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: PINPOINT_SHEET_TYPE,
          ksLocalGovernment: {
            path: { project: projectId },
            field: 'ks.local-government.fund-business-table',
          },
        })}
      >
        <Flex vertical>
          <Table
            dataSource={
              fundBusinessForLocalGovernmentSummaries as SubProjectForKs[]
            }
            rowKey="index"
            bordered
            pagination={false}
            columns={fundBusinessColumns}
          />
          <Box alignSelf="end" paddingTop={4}>
            <Text
              color="secondary"
              text={t('supplementary-for-local-government-fund-business')}
            />
          </Box>
        </Flex>
      </PinpointMarker>
    </Flex>
  );
};
